import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { ServerHost } from "@app/config/app/appConfig";
import { queryClient } from "@app/providers/QueryProvider";
import type { SessionLostReason } from "@app/services/session/sessionEvents";
import { isZustandDevtoolsEnabled } from "./zustandDevtoolsEnabled";
import { REFERENCE_STORE_LOCAL_KEY, useReferenceStore } from "./useReferenceStore";
import { useUserStore } from "./useUserStore";

/** Ключ persist для user store (совместимость с legacy). */
export const USER_STORE_LOCAL_KEY = "current-user-store";

interface AuthState {
    token: string | null;
    server: ServerHost | null;
    /** Сессия готова после bootstrap; не персистится. */
    sessionReady: boolean;
}

interface LogoutOptions {
    /** Причина для session-loss event (если нужен redirect через provider). */
    reason?: SessionLostReason;
}

interface AuthStateActions {
    setToken: (token: string | null) => void;
    setServer: (host: ServerHost | null) => void;
    setSessionReady: (ready: boolean) => void;
    clearToken: () => void;
    clearHost: () => void;
    isAuthenticated: () => boolean;
    /** Очищает данные сессии без UI/redirect. */
    clearSessionState: () => void;
    /** Централизованный logout: очистка stores и query cache. */
    logout: (options?: LogoutOptions) => void;
}

const initialState: AuthState = {
    token: null,
    server: null,
    sessionReady: false,
};

/**
 * App-level store аутентификации и готовности сессии.
 */
export const useAuthStore = create<AuthState & AuthStateActions>()(
    devtools(
        persist(
            (set, get) => ({
                ...initialState,
                setToken: (token) => {
                    set({
                        token,
                        sessionReady: token != null ? false : get().sessionReady,
                    });
                },
                setServer: (host) => {
                    set({ server: host });
                },
                setSessionReady: (ready) => {
                    if (get().sessionReady === ready) {
                        return;
                    }

                    set({ sessionReady: ready });
                },
                clearToken: () => {
                    set({ token: null, sessionReady: false });
                },
                clearHost: () => {
                    set({ server: null, token: null, sessionReady: false });
                },
                isAuthenticated: () => {
                    const { token } = get();
                    return token != null && token.length > 0;
                },
                clearSessionState: () => {
                    set({ token: null, sessionReady: false });
                    try {
                        localStorage.removeItem(USER_STORE_LOCAL_KEY);
                        localStorage.removeItem("session-defaults-store");
                        localStorage.removeItem(REFERENCE_STORE_LOCAL_KEY);
                        localStorage.removeItem("user-store");
                        localStorage.removeItem("cache-image-store");
                    } catch {
                        // ignore
                    }
                    useUserStore.getState().clearStore();
                    useReferenceStore.getState().clearStore();
                    queryClient.clear();
                },
                logout: (options) => {
                    get().clearSessionState();
                    if (options?.reason != null) {
                        void options;
                    }
                },
            }),
            {
                name: "auth-store",
                partialize: (state) => ({
                    token: state.token,
                    server: state.server,
                }),
            },
        ),
        { name: "auth-store", enabled: isZustandDevtoolsEnabled, trace: true },
    ),
);

export default useAuthStore;
